<script setup lang="ts">
import {
  ref,
  computed,
  watch,
} from 'vue';

import { useI18n } from 'vue-i18n';
import { debounce } from 'lodash-es';

import {
  LpDropdown,
  LpCard,
} from '@thehotelsnetwork/lampo';

import SearchBar from '@/components/SearchBar/SearchBar.vue';

const props = defineProps<{
  options: any[],
  loading: boolean,
}>();

const emit = defineEmits(['onSearch', 'onSelect']);

const { t } = useI18n();
const search = ref('');
const searchValue = ref('');
// No results found
const emptySearchResults = computed(() => searchValue.value // a search was made
  && !props.loading // it has finished loading
  && !props.options.length); // result is empty
// fix strange behaviors on search bar click
const searchTriggerUpdate = ref(true);

const searchWatchHandler = () => debounce(() => {
  searchValue.value = search.value;
  emit('onSearch', search.value);
}, 500);

watch(search, searchWatchHandler());
</script>

<template>
  <LpDropdown
    :key="searchTriggerUpdate ? 'true' : 'false'"
    @close="searchTriggerUpdate = !searchTriggerUpdate"
  >
    <!-- SEARCH -->
    <template #btn>
      <SearchBar
        v-model="search"
        class="min-w-dropdown-lg"
      />
    </template>

    <!-- LOADING CASE -->
    <template
      v-if="loading"
      #menu
    >
      <LpCard
        rounded-size="big"
        class="dropdown-list"
      >
        <Loader
          size="medium"
        />
      </LpCard>
    </template>

    <!-- NO RESULTS CASE -->
    <template
      v-else-if="emptySearchResults"
      #menu
    >
      <LpCard
        rounded-size="big"
        class="dropdown-list"
      >
        <p class="text-sm">
          {{ t('No results found') }}
        </p>
      </LpCard>
    </template>

    <!-- RESULTS CASE -->
    <template
      v-else-if="options.length"
      #menu="{ closeMenu }"
    >
      <LpCard
        rounded-size="big"
        class="dropdown-list"
      >
        <LpMarkerBtn
          v-for="option in options"
          :key="option"
          tag="button"
          type="button"
          class="flex w-full px-6"
          @click.prevent="() => {
            emit('onSelect', option);
            search = '';
            closeMenu();
          }"
        >
          <slot
            name="option"
            :option="option"
          />
        </LpMarkerBtn>
      </LpCard>
    </template>
  </LpDropdown>
</template>

<style lang="postcss" scoped>
  .dropdown-list {
    @apply flex flex-col gap-1 min-w-dropdown-lg max-h-96 overflow-y-auto p-2;
  }
</style>
